import { useMutation } from '@tanstack/react-query'
import { MailWarning } from 'lucide-react'
import { useState } from 'react'

import { authApi } from '@/api/endpoints'
import { useToast } from '@/components/ui/Toast'
import { useT, useTx } from '@/i18n'

import { useCurrentUser } from '../hooks/useCurrentUser'
import { VerifyEmailCodeForm } from './VerifyEmailCodeForm'

/**
 * 邮箱未验证的提示条。
 *
 * 只是提示，不拦任何功能 —— 注册完第一件事就被墙住的产品没人会用。
 * 可以手动关掉，关掉只在本次会话里生效（刷新会再出现，这是有意的：
 * 它是个待办，不是广告）。
 */
export function VerifyEmailBanner() {
  const t = useT()
  const tx = useTx()
  const { user } = useCurrentUser()
  const [dismissed, setDismissed] = useState(false)
  const toast = useToast()

  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(),
    onSuccess: (result) =>
      toast.show(result.sent ? t('verifyBanner.sent') : t('verifyBanner.already')),
    onError: () => toast.error(t('verifyBanner.failed')),
  })

  if (!user || user.email_verified !== false || dismissed) return null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface-subtle px-4 py-3 text-[15px]">
      <MailWarning className="size-5 shrink-0 text-ink-muted" aria-hidden />
      {/* 长邮箱在手机上要能断开，不然会顶到「重新发送」上 */}
      <p className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        {tx('verifyBanner.body', {
          email: <span className="font-bold">{user.email}</span>,
        })}
      </p>
      <button
        type="button"
        onClick={() => resend.mutate()}
        disabled={resend.isPending}
        className="shrink-0 font-bold text-brand-ink hover:underline disabled:opacity-50"
      >
        {t('verifyBanner.resend')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 text-ink-muted hover:underline"
      >
        {t('verifyBanner.later')}
      </button>
      {/* 验证码另起一行，对齐正文（让出图标的宽度） */}
      <div className="w-full pl-8">
        <VerifyEmailCodeForm compact />
      </div>
    </div>
  )
}
